import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Kanban, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { get, post } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { formatCurrency, formatNumber } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/common/EmptyState";
import { ErrorState } from "@/components/common/DataStates";
import { CommentDialog } from "@/components/common/CommentDialog";
import { KanbanBoard } from "@/components/crm/KanbanBoard";
import { OpportunityDialog } from "@/components/crm/dialogs";

/** Pure helper: move an opportunity between columns in the cached board (optimistic UI). */
function moveInBoard(board, opp, stage) {
  if (!board) return board;
  const moved = { ...opp, stage_id: stage.id, stage_name: stage.name, stage_key: stage.key, probability: stage.probability, status: stage.is_won ? "won" : stage.is_lost ? "lost" : "open" };
  const stages = board.stages.map((s) => {
    let opportunities = (s.opportunities || []).filter((o) => o.id !== opp.id);
    if (s.id === stage.id) opportunities = [moved, ...opportunities];
    const total_amount = opportunities.reduce((sum, o) => sum + (Number(o.amount) || 0), 0);
    return { ...s, opportunities, count: opportunities.length, total_amount };
  });
  return { ...board, stages };
}

const Kpi = ({ label, value, testId }) => (
  <div className="og-card px-4 py-3" data-testid={testId}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="mt-0.5 font-heading text-xl font-semibold tracking-tight">{value}</div>
  </div>
);

export const PipelineTab = ({ onOpenRecord, openNew, onNewHandled }) => {
  const { t } = useTranslation();
  const { orgId, hasRole } = useAuth();
  const qc = useQueryClient();
  const canEdit = hasRole("member");
  const [pipelineId, setPipelineId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [lostPending, setLostPending] = useState(null); // {opp, stage}
  const [movingId, setMovingId] = useState(null);

  const { data: pipelines } = useQuery({ queryKey: ["crm-pipelines", orgId], queryFn: () => get("/crm/pipelines"), enabled: !!orgId, staleTime: 60_000 });
  const boardKey = useMemo(() => ["crm-pipeline", orgId, pipelineId || "default"], [orgId, pipelineId]);
  const { data: board, isLoading, isError, error, refetch, isFetching } = useQuery({ queryKey: boardKey, queryFn: () => get(`/crm/pipeline${pipelineId ? `?pipeline_id=${pipelineId}` : ""}`), enabled: !!orgId });

  const move = useMutation({
    mutationFn: ({ opp, stage, lost_reason }) => post(`/crm/opportunities/${opp.id}/move`, { stage_id: stage.id, lost_reason: lost_reason || null }),
    onMutate: async ({ opp, stage }) => {
      setMovingId(opp.id);
      await qc.cancelQueries({ queryKey: boardKey });
      const previous = qc.getQueryData(boardKey);
      qc.setQueryData(boardKey, (b) => moveInBoard(b, opp, stage));
      return { previous };
    },
    onError: (err, _vars, ctx) => { if (ctx?.previous) qc.setQueryData(boardKey, ctx.previous); toast.error(`${t("crm.pipeline.moveError")} — ${errorMessage(err)}`); },
    onSuccess: (_d, { stage }) => toast.success(t("crm.pipeline.moved", { stage: stage.name })),
    onSettled: () => { setMovingId(null); qc.invalidateQueries({ queryKey: ["crm-pipeline"] }); qc.invalidateQueries({ queryKey: ["crm-opportunities"] }); qc.invalidateQueries({ queryKey: ["crm-record"] }); },
  });
  const closeMut = useMutation({
    mutationFn: ({ opp, status }) => post(`/crm/opportunities/${opp.id}/close`, { status }),
    onSuccess: () => { toast.success(t("crm.opportunity.closed")); qc.invalidateQueries({ queryKey: ["crm-pipeline"] }); qc.invalidateQueries({ queryKey: ["crm-opportunities"] }); },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const requestMove = (opp, stage) => {
    if (!canEdit || !stage || stage.id === opp.stage_id) return;
    if (stage.is_lost) { setLostPending({ opp, stage }); return; }
    move.mutate({ opp, stage });
  };

  const total = board ? board.stages.reduce((n, s) => n + (s.count || 0), 0) : 0;

  return (
    <div className="space-y-4" data-testid="crm-pipeline-tab">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {pipelines?.length > 1 && (
            <Select value={pipelineId || pipelines.find((p) => p.is_default)?.id} onValueChange={setPipelineId}>
              <SelectTrigger className="w-full max-w-[200px] bg-card" data-testid="crm-pipeline-select"><SelectValue placeholder={t("crm.pipeline.selectPipeline")} /></SelectTrigger>
              <SelectContent className="bg-card">{pipelines.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`crm-pipeline-option-${p.key}`}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <p className="text-xs text-muted-foreground">{t("crm.pipeline.hint")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" className="bg-card" onClick={() => refetch()} disabled={isFetching} data-testid="crm-pipeline-refresh"><RefreshCw className={isFetching ? "mr-2 h-3.5 w-3.5 animate-spin" : "mr-2 h-3.5 w-3.5"} />{t("common.reload")}</Button>
          {canEdit && <Button size="sm" onClick={() => setEditing({})} data-testid="crm-pipeline-new-opportunity"><Plus className="mr-2 h-4 w-4" />{t("crm.newOpportunity")}</Button>}
        </div>
      </div>

      {isError && <ErrorState message={errorMessage(error)} onRetry={refetch} testId="crm-pipeline-error" />}
      {isLoading && (
        <div className="space-y-4" data-testid="crm-pipeline-loading">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          <div className="flex gap-3 overflow-hidden">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-64 w-[272px] shrink-0" />)}</div>
        </div>
      )}
      {board && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Kpi label={t("crm.opportunities")} value={formatNumber(total)} testId="crm-pipeline-kpi-count" />
            <Kpi label={t("crm.pipeline.openValue")} value={formatCurrency(board.open_value, board.currency)} testId="crm-pipeline-kpi-open" />
            <Kpi label={t("crm.pipeline.wonValue")} value={formatCurrency(board.won_value, board.currency)} testId="crm-pipeline-kpi-won" />
          </div>
          {total === 0 && <EmptyState icon={Kanban} title={t("crm.pipeline.empty")} hint={t("crm.pipeline.emptyHint")} testId="crm-pipeline-empty" action={canEdit && <Button size="sm" onClick={() => setEditing({})} data-testid="crm-pipeline-empty-new"><Plus className="mr-2 h-4 w-4" />{t("crm.newOpportunity")}</Button>} />}
          <KanbanBoard board={board} canEdit={canEdit} movingId={movingId} onMove={requestMove} onOpen={(opp) => onOpenRecord("opportunity", opp.id)} onEdit={(opp) => setEditing(opp)} onClose={(opp, status) => closeMut.mutate({ opp, status })} />
          {board.unstaged?.length > 0 && (
            <div className="og-card p-4" data-testid="crm-pipeline-unstaged">
              <div className="og-section-label mb-2">{t("crm.pipeline.unstaged")}</div>
              <ul className="flex flex-wrap gap-2">{board.unstaged.map((o) => <li key={o.id}><Button variant="outline" size="sm" className="bg-card" onClick={() => onOpenRecord("opportunity", o.id)}>{o.name}</Button></li>)}</ul>
            </div>
          )}
        </>
      )}

      <OpportunityDialog open={!!editing || !!openNew} onOpenChange={(o) => { if (!o) { setEditing(null); onNewHandled?.(); } }} initial={editing?.id ? editing : null} pipelineId={pipelineId || board?.pipeline?.id} />
      <CommentDialog open={!!lostPending} onOpenChange={(o) => !o && setLostPending(null)} title={lostPending ? t("crm.pipeline.dropHere", { stage: lostPending.stage.name }) : ""} label={t("crm.opportunity.lostReason")} required={false} loading={move.isPending} confirmLabel={t("crm.moveTo")} onConfirm={(text) => { const p = lostPending; setLostPending(null); move.mutate({ opp: p.opp, stage: p.stage, lost_reason: text }); }} testId="crm-pipeline-lost-dialog" />
    </div>
  );
};
